import React from 'react';
import { FileTextIcon, FolderIcon, MapPinIcon, SparklesIcon, BellIcon, ImageIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { DEMO_CITIZEN, type DemoRequestDetail } from '../../data/brossardRequests';
interface DemoRequestInfosProps {
  request: DemoRequestDetail;
  statusBadge: React.ReactNode;
}

/** "Infos" tab for the Brossard demo requests, rendered from data. */
export function DemoRequestInfos({
  request,
  statusBadge
}: DemoRequestInfosProps) {
  const {
    t,
    lang
  } = useLanguage();
  return <div className="space-y-8" data-id="element-5329" data-mp="1kjr6ly9sdnpq">
      <div className="flex justify-between items-start gap-4" data-id="element-5330" data-mp="1y91dizvn5jlf">
        <div className="flex gap-4" data-id="element-5331" data-mp="1yam8urps56kc">
          <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-5332" data-mp="1tiupp28ye348">
            <FolderIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-5333" data-mp="15ryudv5agps8" />
          </div>
          <div data-id="element-5334" data-mp="10ugt8qxijeus">
            <h2 className="text-xl font-bold text-slate-900" data-id="element-5335" data-mp="10svz32f41xxg">{request.title[lang]}</h2>
            <div className="text-sm text-slate-500 mt-1 flex items-center gap-2 flex-wrap" data-id="element-5336" data-mp="c6ew8b814lgt">
              <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium border border-slate-200" data-id="element-5337" data-mp="h46jut1n5lf7">
                #{request.reqId}
              </span>
              <span data-id="element-5338" data-mp="lxsopz1kcl0g">{request.category[lang]}</span>
            </div>
            <div className="text-sm text-slate-400 mt-1" data-id="element-5339" data-mp="qtzxdzlcgam3">{request.service[lang]}</div>
          </div>
        </div>
        {statusBadge}
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-5340" data-mp="3ub89e2te4g8w">
        <div className="rounded-lg border border-slate-200 p-3" data-id="element-5341" data-mp="3ubrtnq6ijgq3">
          <div className="text-xs text-slate-400" data-id="element-5342" data-mp="34p3n22evghg">{t('Créée le', 'Created on')}</div>
          <div className="font-medium text-slate-800 mt-0.5" data-id="element-5343" data-mp="76aaa5ocqyj7">{request.createdOn[lang]}</div>
          <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1" data-id="element-5344" data-mp="1ekdrdvnhojjb">
            <SparklesIcon className="w-3 h-3 text-[#4F46E5]" data-id="element-5345" data-mp="1ilp8fcsarwfw" />
            {t('Via ATNA, portail citoyen', 'Via ATNA, citizen portal')}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 p-3" data-id="element-5346" data-mp="3fks4qgys4tcf">
          <div className="text-xs text-slate-400" data-id="element-5347" data-mp="3ao1pfvusu1s2">{t('Échéance', 'Due date')}</div>
          <div className="font-medium text-slate-800 mt-0.5" data-id="element-5348" data-mp="3omrlr99snxfd">{request.dueOn[lang]}</div>
          {request.dueNote && <div className="text-xs text-slate-500 mt-1" data-id="element-5349" data-mp="3jnf2szm1nai2">{request.dueNote[lang]}</div>}
        </div>
      </div>

      <section data-id="element-5350" data-mp="2vxkrimhdhtzx">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5351" data-mp="2vz5loazrzax9">
          {t('Description', 'Description')}
        </h3>
        <div className="p-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm leading-relaxed" data-id="element-5352" data-mp="35o5jvs6ge2ae">
          « {request.description[lang]} »
          {request.descriptionNote && <p className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1" data-id="element-5353" data-mp="2d7uvjw1vj26f">
              <SparklesIcon className="w-3 h-3 text-[#4F46E5]" data-id="element-5354" data-mp="2d5ry4qye66ro" />
              {request.descriptionNote[lang]}
            </p>}
        </div>
        {request.atnaContext && <div className="mt-3 flex gap-2 rounded-xl border border-indigo-100 bg-indigo-50/50 p-3 text-sm text-slate-700" data-id="element-5355" data-mp="npfvicbk6fdz">
            <SparklesIcon className="w-4 h-4 text-[#4F46E5] flex-shrink-0 mt-0.5" data-id="element-5356" data-mp="eq15tf37u8uj" />
            <div data-id="element-5357" data-mp="eny7m7rwt9eq">
              <div className="text-xs font-semibold text-[#4F46E5] mb-0.5" data-id="element-5358" data-mp="jp5fs7xysalx">
                {t('Contexte ATNA', 'ATNA context')}
              </div>
              {request.atnaContext[lang]}
            </div>
          </div>}
      </section>

      <div className="grid grid-cols-2 gap-8" data-id="element-5359" data-mp="1bf0gt1v6poqf">
        <section data-id="element-5360" data-mp="xgitv6d3sg9k">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5361" data-mp="xh23lzslnyci">
            {t('Demandeur', 'Requester')}
          </h3>
          <div className="flex items-start gap-3" data-id="element-5362" data-mp="12dshqhkugjva">
            <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" data-id="element-5363" data-mp="3i687xh5zfy17">
              {DEMO_CITIZEN.initials}
            </div>
            <div data-id="element-5364" data-mp="6amx5ojmdyg0">
              <div className="font-semibold text-slate-900 text-sm" data-id="element-5365" data-mp="6b52dao9fn02">{DEMO_CITIZEN.name}</div>
              <div className="text-sm text-slate-500 mt-0.5" data-id="element-5366" data-mp="1ic0qvo38us7">{DEMO_CITIZEN.phone}</div>
              <div className="text-sm text-slate-500 break-all" data-id="element-5367" data-mp="3tme9fkjyqc8r">{DEMO_CITIZEN.email}</div>
              {request.followUp && <div className="text-xs text-slate-500 mt-1.5 inline-flex items-center gap-1" data-id="element-5368" data-mp="3opnuj0jw9mox">
                  <BellIcon className="w-3 h-3" data-id="element-5369" data-mp="p7o4hg0ncan4" />
                  {t('Suivi :', 'Follow-up:')} {request.followUp[lang]}
                </div>}
            </div>
          </div>
        </section>
        <section data-id="element-5370" data-mp="g6q2125uwb6n">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5371" data-mp="gadvk1bawxld">
            {t('Emplacement', 'Location')}
          </h3>
          <div className="flex items-start gap-2" data-id="element-5372" data-mp="30ys5gb6tp5ld">
            <MapPinIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-5373" data-mp="30wfi1iiaq30x" />
            <div className="text-sm text-slate-700" data-id="element-5374" data-mp="2vy44oxsslady">
              {request.addressLines.map(line => <div key={line} data-id="element-5375" data-mp="2ltdyugu9pm4g">{line}</div>)}
              <div className="text-xs text-slate-400 mt-1 tabular-nums" data-id="element-5376" data-mp="2qvmuxm2ft4as">
                {request.coords[0].toFixed(4)}, {request.coords[1].toFixed(4)}
              </div>
            </div>
          </div>
        </section>
      </div>

      <section data-id="element-5377" data-mp="30lpnjl5ncg8l">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5378" data-mp="30m7rz52gq0rl">
          {t('Pièces jointes', 'Attachments')}
        </h3>
        {request.attachments.length === 0 ? <p className="text-sm text-slate-400" data-id="element-5379" data-mp="39mw5lyny1z3y">{t('Aucune pièce jointe', 'No attachment')}</p> : <div className="flex flex-wrap gap-3" data-id="element-5380" data-mp="3jj8xqs3uc4wl">
            {request.attachments.map(file => file.url ? <a key={file.name} href={file.url} target="_blank" rel="noreferrer" className="group w-40 rounded-lg border border-slate-200 overflow-hidden hover:border-[#4F46E5]" data-id="element-5381" data-mp="1k6og2fozum8d">
                  <img src={file.url} alt={request.title[lang]} className="w-full h-24 object-cover" data-id="element-5382" data-mp="1c5yty0l47k9u" />
                  <div className="px-2 py-1.5" data-id="element-5383" data-mp="1r39496m0uk3z">
                    <div className="text-xs font-medium text-slate-700 truncate" data-id="element-5384" data-mp="1r4110krcunvu">{file.name}</div>
                    <div className="text-[11px] text-slate-400" data-id="element-5385" data-mp="1m2bkwqp9zk0w">{file.size}</div>
                  </div>
                </a> : <div key={file.name} className="w-40 rounded-lg border border-slate-200 p-3 flex items-center gap-2" data-id="element-5386" data-mp="1v4kt39x2n1b4">
                  <span className="w-9 h-9 rounded-md bg-slate-100 text-slate-400 flex items-center justify-center flex-shrink-0" data-id="element-5387" data-mp="29v1w2xpctza4">
                    {file.name.endsWith('.jpg') ? <ImageIcon className="w-4 h-4" data-id="element-5388" data-mp="2iuoynmttlb4r" /> : <FileTextIcon className="w-4 h-4" data-id="element-5389" data-mp="2irsx7oirk4bk" />}
                  </span>
                  <div className="min-w-0" data-id="element-5390" data-mp="2eu52alh2mbjo">
                    <div className="text-xs font-medium text-slate-700 truncate" data-id="element-5391" data-mp="2xgufg5qlkvw2">{file.name}</div>
                    <div className="text-[11px] text-slate-400" data-id="element-5392" data-mp="32ffe40q7b7ie">{file.size}</div>
                  </div>
                </div>)}
          </div>}
      </section>

      <section data-id="element-5393" data-mp="2ter0360t575i">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5394" data-mp="2tehhw4m084ad">
          {t('Historique', 'History')}
        </h3>
        <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4" data-id="element-5395" data-mp="2eojona97woeb">
          {request.history.map((item, i) => <li key={i} className="relative pl-5" data-id="element-5396" data-mp="2jio1ub92tsnu" data-mpi={i}>
              <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 border-white ${item.current ? 'bg-[#4F46E5]' : 'bg-slate-300'}`} data-id="element-5397" data-mp="25qg8yukwyluf" />
              <div className={`text-sm ${item.current ? 'font-semibold text-slate-900' : 'text-slate-700'}`} data-id="element-5398" data-mp="1vs0ovbkb0cuf">
                {item.label[lang]}
              </div>
              <div className="text-xs text-slate-400" data-id="element-5399" data-mp="2g8l9q8rotw2u">{item.date[lang]}</div>
            </li>)}
        </ol>
      </section>
    </div>;
}